import React, { Component, createRef } from 'react';
import PropTypes from 'prop-types';
import generateUniqueId from '@insight/toolkit-utils/lib/generators/generateUniqueId';
import cn from 'classnames';

import Tabs from './Tabs';
import Tab from './Tab';

// TO-DO: Keyboard Navigation
// - Arrow keys, when focus is on a selected tab, cycle selection to the next or previous tab.
// - Tab key, when focus is before the tab list, moves focus to the selected tab.
// - Tab key, when focus is on selected tab, moves focus into the selected tab’s associated tab panel or to the next focusable element on the page if that panel has no focusable elements.
// - Shift + Tab keys, when focus is on first element in a tab panel, moves focus back to the selected tab.

export default class TabManager extends Component {
  constructor(props) {
    super(props);

    const {
      autoRotate,
      autoRotateInterval,
      initialSelectedTab,
      selectedTab,
      tabs,
    } = props;

    this.rootId = 'tab-manager-id--' + generateUniqueId();
    this.tabs = tabs;
    this.autoRotate = autoRotate;
    this.autoRotateInterval = autoRotateInterval;
    this.autoRotateTimeoutHandle = 0;

    // Only track selection state if it is not controlled externally
    if (!selectedTab) {
      this.state = {
        selectedTabId:
          (initialSelectedTab && initialSelectedTab.id) || tabs[0].id,
      };
    }
  }

  /**
   * Setup resize listeners and trigger an initial refresh.
   */
  componentDidMount() {
    if (this.autoRotate) {
      this.startAutoRotate();
    }
  }

  startAutoRotate() {
    if (
      !this.autoRotate ||
      this.tabs.length == 0 ||
      !!this.autoRotateTimeoutHandle
    )
      return;
    const self = this;
    const autoRotateFun = function () {
      const selectedTabIndex = self.tabs.findIndex(
        ({ id }) => id === self.state.selectedTabId
      );
      const nextPage = (selectedTabIndex + 1) % self.tabs.length;
      self.autoRotateTimeoutHandle = setTimeout(
        autoRotateFun,
        self.autoRotateInterval
      );
      const selected = self.tabs[nextPage];

      self.setState({
        selectedTabId: selected.id,
        pauseTabEffect: true,
        pauseEffect: true,
      });
    };
    this.autoRotateTimeoutHandle = setTimeout(
      autoRotateFun,
      self.autoRotateInterval
    );
  }
  cancelAutoRotate() {
    const self = this;
    if (!self.autoRotate || self.isCarousel || !self.autoRotateTimeoutHandle)
      return;
    clearTimeout(self.autoRotateTimeoutHandle);
    self.autoRotateTimeoutHandle = 0;
  }

  onTabClick = (selected) => {
    const { onTabClick, selectedTab } = this.props;
    if (onTabClick) {
      onTabClick(selected);
    }

    if (!selectedTab) {
      this.setState({
        selectedTabId: selected.id,
        pauseTabEffect: true,
        pauseEffect: true,
      });
    }
  };

  render() {
    const {
      tabsAlign,
      autoRotate,
      autoRotateInterval,
      className,
      tabClassName,
      contentClassName,
      expand,
      initialSelectedTab,
      onTabClick,
      selectedTab: externalSelectedTab,
      tabs,
      vertical,
      ...rest
    } = this.props;
    const self = this;

    // Allow the consumer to control tab selection
    const selectedTab =
      externalSelectedTab ||
      tabs.find(({ id }) => id === this.state?.selectedTabId);
    const { content: selectedTabContent, id: selectedTabId } = selectedTab;

    let managerLayout = '';
    if (vertical) {
      managerLayout = 'c-tab-manager--vertical';
    } else {
      managerLayout = 'c-tab-manager--' + tabsAlign;
    }
    const managerProps = {
      'aria-labelledby': selectedTabId,
      id: this.rootId,
      role: 'tabpanel',
      onMouseLeave: self.startAutoRotate.bind(self),
      onMouseEnter: self.cancelAutoRotate.bind(self),
    };

    if (this.state?.pauseTabEffect) {
      setTimeout(() => {
        this.setState({ pauseTabEffect: false });
      }, 50);
      setTimeout(() => {
        this.setState({ pauseEffect: false });
      }, 250);
    }

    let managerClassName = cn(
      'c-tab-manager',
      {
        'c-tab-manager__pause-tab-effects': this.state?.pauseTabEffect,
        'c-tab-manager__pause-effects': this.state?.pauseEffect,
      },
      managerLayout
    );

    return (
      <div className={managerClassName} {...managerProps}>
        <Tabs expand={expand} className={tabClassName}>
          {tabs.map((tab, index) => {
            const {
              content,
              contentClassName,
              id,
              name,
              tabClassName,
              tabIndex,
              ...tabProps
            } = tab;
            const props = {
              'aria-controls': `${id}`,
              id,
              isSelected: tab === selectedTab,
              key: id,
              onClick: () => this.onTabClick(tab),
              tabIndex,
              ...tabProps,
            };

            return <Tab {...props}>{name}</Tab>
          })}
        </Tabs>

        <div
          aria-labelledby={selectedTabId}
          className={cn('c-tab__content', contentClassName)}
          id={`${this.rootId}-${selectedTabId}`}
          role="tabpanel"
        >
          {selectedTabContent}
        </div>
      </div>
    );
  }
}

TabManager.propTypes = {
  autoRotate: PropTypes.bool,
  autoRotateInterval: PropTypes.number,
  className: PropTypes.string,
  contentClassName: PropTypes.string,
  expand: PropTypes.bool,
  initialSelectedTab: PropTypes.object,
  onTabClick: PropTypes.func,
  selectedTab: PropTypes.object,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      content: PropTypes.node.isRequired,
      contentClassName: PropTypes.string,
      id: PropTypes.string.isRequired,
      name: PropTypes.oneOfType([PropTypes.element, PropTypes.string])
        .isRequired,
      tabClassName: PropTypes.string,
    })
  ).isRequired,
  tabsAlign: PropTypes.string,
  vertical: PropTypes.bool,
};

TabManager.defaultProps = {
  autoRotate: false,
  autoRotateInterval: 5000,
  className: '',
  contentClassName: '',
  expand: false,
  tabsAlign: 'center',
  vertical: false,
};
